import type { ReactNode } from "react";
import { cn } from "./cn";

/**
 * An ageing bucket set, rendered with **however many buckets arrived** (`003` R3, FR-014).
 *
 * ## There are deliberately no `days0To30`-style props
 *
 * Bucket boundaries are backend configuration (backend FR-071), and `customers-admin.md` is
 * explicit: "a front end must render from the returned bucket set rather than hard-coding four
 * columns." The typed four-column row type-checks, renders correctly today, and is wrong the
 * first time the client re-cuts the buckets. Taking an array makes that version *unwritable*
 * rather than merely discouraged — the same technique as `PriceDisplay`.
 *
 * ## The bar does no arithmetic, in JS or in CSS
 *
 * Segments are sized with `flex-grow: value`. Flexbox distributes free space in proportion to the
 * grow factors, so the browser performs the division and this repo computes no percentage, no
 * total and no share of one (constitution I). A bucket at zero gets a grow factor of zero and
 * correctly disappears; every bucket at zero leaves an empty track, which is the truth.
 *
 * ## `display` is separate from `value`, and is already formatted
 *
 * `value` sizes the segment; `display` is the figure the reader sees, formatted by the caller with
 * the Indian-grouping formatter at the API's precision (§8.5). Same reasoning as `StatCard`: this
 * component must not be the second place a money value gets formatted or rounded.
 */
export type Bucket = {
  /** The API's own label for the bucket — "0–30 days", or whatever the configuration says. */
  label: string;
  /** The API's figure, used only to size the segment. Never rendered. */
  value: number;
  /** The figure as the reader sees it. Formatted by the caller. */
  display: ReactNode;
};

/**
 * Severity ramp by position, because severity *is* position: later buckets are older debt. Not a
 * per-bucket colour lookup, which would need to know the boundaries this component refuses to know.
 * Beyond the ramp's length every further bucket clamps to the last tone.
 */
const RAMP = [
  "bg-gray-400",
  "bg-primary",
  "bg-orange",
  "bg-red/70",
  "bg-red",
] as const;

const tone = (index: number) => RAMP[Math.min(index, RAMP.length - 1)];

export function BucketBar({
  buckets,
  label,
}: {
  buckets: readonly Bucket[];
  /** Names the figures for a screen reader — "Ageing of outstanding balance". */
  label: string;
}) {
  return (
    <section data-slot="bucket-bar" aria-label={label} className="flex flex-col gap-3">
      {/* aria-hidden: the list below carries every label and figure as text. A screen reader that
          also walked the segments would hear each bucket twice, and the segments have no text of
          their own to hear. */}
      <div
        aria-hidden
        className="bg-gray-100 flex h-2.5 w-full overflow-hidden rounded-full"
        data-slot="bucket-bar-track"
      >
        {buckets.map((bucket, index) => (
          <span
            key={bucket.label}
            data-slot="bucket-bar-segment"
            className={tone(index)}
            style={{ flexGrow: bucket.value }}
          />
        ))}
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-2">
        {buckets.map((bucket, index) => (
          <div key={bucket.label} className="flex min-w-0 flex-col gap-1">
            <dt className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <span aria-hidden className={cn("size-2 shrink-0 rounded-full", tone(index))} />
              {bucket.label}
            </dt>
            <dd className="font-mono text-sm font-semibold tabular-nums">{bucket.display}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
